import type { ContactDetailsFields } from '@features/page/hooks/use-contact-details'
import { RequiredIndicator } from '@shared/ui/form-label-indicators'
import { useIntents } from '@vp/contact-channels'
import { FormField, FormInputGroup, FormLabel, Listbox, ListboxButton, ListboxList, ListboxOption, ListboxPopover, Typography } from '@vp/swan'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface IntentFieldProps {
  control: Control<ContactDetailsFields>
}

export const IntentField = ({ control }: IntentFieldProps) => {
  const { t } = useTranslation()
  const { data: intents, isLoading: isLoadingIntents } = useIntents()
  const handleSelectionChange = (field: { onChange: (value: string) => void }, keys: Set<string | number> | string | number) => {
    const val = keys instanceof Set ? [...keys][0] : keys
    const selectedId = String(val ?? '')
    field.onChange(selectedId)
  }

  return (
    <FormField data-testid="intent-field">
      <FormLabel>
        {t('contact_us.page.steps.details.fields.contact_reason_placeholder')}
        <RequiredIndicator />
      </FormLabel>
      <Controller
        name="intentId"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormInputGroup>
            <Listbox
              fullWidth
              value={field.value}
              selectedKeys={new Set<string | number>([field.value])}
              onSelectionChange={(keys) => handleSelectionChange(field, keys)}
            >
              <ListboxButton data-testid="intent-listbox-button" placeholder={t('contact_us.page.steps.details.fields.contact_reason_placeholder')} />
              <ListboxPopover data-testid="intent-listbox-popover">
                <ListboxList>
                  {isLoadingIntents ? (
                    <ListboxOption disabled key="loading">
                      {t('contact_us.page.steps.details.fields.contact_reason_loading')}
                    </ListboxOption>
                  ) : (
                    intents?.map((intent) => (
                      <ListboxOption key={intent.id}>
                        <Typography as="span" data-testid={`intent-option-${intent.id}`} data-intent-id={intent.id}>
                          {intent.id === 'help_with_my_order' ? t('contact_us.page.steps.details.fields.contact_reason_existing_order') : intent.customerFacing}
                        </Typography>
                      </ListboxOption>
                    ))
                  )}
                </ListboxList>
              </ListboxPopover>
            </Listbox>
          </FormInputGroup>
        )}
      />
    </FormField>
  )
}
